/* eslint-disable */

import { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
// chakra imports
import { Box, Button, Collapse, Divider, Flex, HStack, IconButton, Text, useColorModeValue } from '@chakra-ui/react';
import { MdDragIndicator, MdExpandLess, MdExpandMore, MdStar, MdStarBorder } from 'react-icons/md';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { layouts, RType } from 'routes/types';
import { isRouteActive as isActiveRoute } from '../utils/activeRoute';
import { translate } from '../../../i18n';
import { useAuth } from '../../../features/auth/AuthProvider';
import { useAppSettings } from '../../../contexts/AppSettingsContext';
import { useFeatureDiscovery } from '../../../features/walkthrough/hooks/useFeatureDiscovery';
import FeatureBeacon from '../../../features/walkthrough/components/FeatureBeacon';
import { useAdminSidebarFavorites } from '../hooks/useAdminSidebarFavorites';
import { SidebarMode } from '../hooks/useAdminSidebarMode';
import { useSelectedOrganization } from '../../../features/organization/hooks/useSelectedOrganization';

const SIDEBAR_GROUPS_STORAGE_KEY = 'admin-sidebar-expanded-groups';

// Defines the display order of groups in the sidebar
const GROUP_ORDER = [
	'sidebar.groups.organization',
	'sidebar.groups.users',
	'sidebar.groups.content',
	'sidebar.groups.commerce',
	'sidebar.groups.data',
	'sidebar.groups.system',
];

function SortableNavItem({ route, children }: { route: RType; children: React.ReactNode }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: route.path });
	const dragHandleColor = useColorModeValue('secondaryGray.400', 'secondaryGray.600');

	return (
		<Box
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}
			position='relative'
			role='group'
		>
			{children}
			<Box
				{...attributes}
				{...listeners}
				position='absolute'
				left='0'
				top='50%'
				transform='translateY(-50%)'
				cursor='grab'
				opacity={0}
				_groupHover={{ opacity: 0.6 }}
				transition='opacity 0.15s'
				px='2px'
				py='4px'
				aria-label={translate('sidebar.reorder') as string}
			>
				<Box as={MdDragIndicator} w='14px' h='14px' color={dragHandleColor} />
			</Box>
		</Box>
	);
}

export function SidebarLinks({ routes, enableFavorites, sidebarMode, onSwitchMode }: {
	routes: RType[];
	enableFavorites?: boolean;
	sidebarMode?: SidebarMode;
	onSwitchMode?: (mode: SidebarMode) => void;
}) {
	const { aesthetic } = useAppSettings();
	const isClubhouse = aesthetic === 'clubhouse';
	const { currentUser } = useAuth();
	const uid = currentUser?.sub as string | undefined;
	const { hasVisited } = useFeatureDiscovery(uid);
	const { selectedOrganization } = useSelectedOrganization();
	const favorites = useAdminSidebarFavorites();
	const { favoritePaths, toggleFavorite, isFavorite, reorderFavorites } = enableFavorites
		? favorites
		: { favoritePaths: [] as string[], toggleFavorite: () => {}, isFavorite: () => false, reorderFavorites: () => {} };

	const mode = sidebarMode ?? 'full';

	//   Chakra color mode
	let location = useLocation();
	let activeColor = useColorModeValue('gray.700', 'white');
	let inactiveColor = useColorModeValue('secondaryGray.600', 'secondaryGray.600');
	let activeIcon = useColorModeValue('brand.500', 'white');
	let textColor = useColorModeValue('secondaryGray.500', 'white');
	let brandColor = useColorModeValue('brand.500', 'brand.400');
	let starColor = useColorModeValue('gray.400', 'gray.500');
	let groupHeaderColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
	let groupDividerColor = useColorModeValue('brand.200', 'brand.700');
	let activeItemBg = useColorModeValue('brand.50', 'whiteAlpha.100');

	// Most-specific match wins — see utils/activeRoute.ts (and its test) for why a
	// parent such as `/court-recording` must not light up under
	// `/court-recording/venue-agents`.
	const isRouteActive = (route: RType) => isActiveRoute(location.pathname, route, routes);

	const visibleRoutes = routes.filter((r) => {
		if (!layouts.includes(r.layout)) return false;
		// Hide org-feature-gated routes when the selected org lacks the flag.
		if (r.requiresOrgFeature && !selectedOrganization?.[r.requiresOrgFeature]) return false;
		return true;
	});
	const favoriteRoutes: RType[] = enableFavorites
		? favoritePaths
			.map((path) => visibleRoutes.find((r) => r.path === path))
			.filter((r): r is RType => r != null)
		: [];

	// In full mode, show all routes in groups (favorites are just starred, not separated)
	const allRoutesForFullMode = visibleRoutes;
	// In full mode, we need grouped routes for the collapsible sections
	const otherRoutes = enableFavorites && mode === 'focused'
		? visibleRoutes.filter((r) => !favoritePaths.includes(r.path))
		: visibleRoutes;

	// Group routes by their group key, preserving order
	const hasGroups = otherRoutes.some((r) => r.group);

	// Current groups that actually have routes
	const currentGroups = new Set(otherRoutes.map((r) => r.group).filter(Boolean));

	// Store raw user preferences from localStorage — independent of routes so it
	// survives initial renders where routes may not be loaded yet.
	const [storedPrefs, setStoredPrefs] = useState<Record<string, boolean>>(() => {
		try {
			const raw = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
			if (!raw) return {};
			const parsed = JSON.parse(raw);
			if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
			return parsed;
		} catch {
			return {};
		}
	});

	// Derive effective expanded state reactively from stored prefs + current routes + active path.
	// Recomputes whenever routes load, prefs change, or navigation occurs.
	const expandedGroups = useMemo(() => {
		const expanded: Record<string, boolean> = {};
		for (const group of GROUP_ORDER) {
			if (!currentGroups.has(group)) continue;
			if (group in storedPrefs) {
				// User has explicitly toggled this group before — respect their choice
				expanded[group] = storedPrefs[group];
			} else {
				// No stored preference: auto-expand if it contains the active route
				const groupRoutes = otherRoutes.filter((r) => r.group === group);
				expanded[group] = groupRoutes.some((r) => isRouteActive(r));
			}
		}
		return expanded;
	}, [storedPrefs, currentGroups.size, otherRoutes.length, location.pathname]);

	const toggleGroup = (group: string) => {
		setStoredPrefs((prev) => {
			const next = { ...prev, [group]: !expandedGroups[group] };
			// Prune keys for groups that no longer exist
			const pruned: Record<string, boolean> = {};
			for (const [k, v] of Object.entries(next)) {
				if (currentGroups.has(k)) pruned[k] = v;
			}
			try {
				localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(pruned));
			} catch { /* quota exceeded or private browsing — ignore */ }
			return pruned;
		});
	};

	// DnD sensors for Focused mode
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const fromIndex = favoritePaths.indexOf(active.id as string);
		const toIndex = favoritePaths.indexOf(over.id as string);
		if (fromIndex !== -1 && toIndex !== -1) {
			reorderFavorites(fromIndex, toIndex);
		}
	}, [favoritePaths, reorderFavorites]);

	const createLink = (route: RType, index: number, showStar = true) => {
		if (!layouts.includes(route.layout)) return null;
		const isFav = enableFavorites && isFavorite(route.path);
		const isActive = isRouteActive(route);
		const showBeacon = !isActive && !hasVisited(route.path);

		// Clubhouse: full-width background highlight, no right-side bar, no stars
		if (isClubhouse) {
			return (
				<NavLink key={`${route.path}-${index}`} to={route.layout + route.path} className='sidebar-nav-link' data-tour={`sidebar-link${route.path}`} style={{ display: 'block', width: '100%' }}>
					<Flex
						w='100%'
						alignItems='center'
						py='8px'
						ps='10px'
						pe='12px'
						mx='0'
						borderRadius='10px'
						bg={isActive ? activeItemBg : 'transparent'}
						transition='background 0.15s ease'
						_hover={{ bg: isActive ? activeItemBg : 'secondaryGray.400' }}
						role='group'
					>
						{route.icon && (
							<Box
								color={isActive ? activeIcon : textColor}
								me='14px'
								flexShrink={0}
								display='flex'
								alignItems='center'
								mt='-1px'
							>
								{route.icon}
							</Box>
						)}
						<Text
							me='auto'
							minW={0}
							noOfLines={1}
							fontSize='14px'
							color={isActive ? activeColor : textColor}
							fontWeight={isActive ? '600' : '400'}
						>
							{route.name}
						</Text>
						{showBeacon && <Box mx={1}><FeatureBeacon /></Box>}
						{enableFavorites && showStar && (
							<IconButton
								aria-label={isFav ? translate('sidebar.remove_from_favorites') : translate('sidebar.add_to_favorites')}
								size='xs'
								variant='ghost'
								flexShrink={0}
								minW='24px'
								w='24px'
								h='24px'
								color={isFav ? 'yellow.400' : starColor}
								icon={<Box as={isFav ? MdStar : MdStarBorder} w='14px' h='14px' />}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									toggleFavorite(route.path);
								}}
								opacity={isFav ? 1 : 0}
								_groupHover={{ opacity: isFav ? 1 : 0.5 }}
								_hover={{ color: 'yellow.500', bg: 'transparent', opacity: 1 }}
								transition='opacity 0.15s ease'
							/>
						)}
					</Flex>
				</NavLink>
			);
		}

		// Classic: original layout with right-side bar + stars
		return (
			<NavLink key={`${route.path}-${index}`} to={route.layout + route.path} className='sidebar-nav-link' data-tour={`sidebar-link${route.path}`} style={{ display: 'block', width: '100%' }}>
				{route.icon ? (
					<Box w='100%'>
						<HStack w='100%' spacing='26px' py='5px' ps='10px' alignItems='stretch'>
							<Flex w='100%' minW={0} alignItems='center' gap={0}>
								<Box
									color={isActive ? activeIcon : textColor}
									me='18px'
									flexShrink={0}
									display='flex'
									alignItems='center'
									mt='-1px'>
									{route.icon}
								</Box>
								<Text
									me='auto'
									minW={0}
									noOfLines={1}
									color={isActive ? activeColor : textColor}
									fontWeight={isActive ? 'bold' : 'normal'}>
									{route.name}
								</Text>
								{showBeacon && <Box mx={1}><FeatureBeacon /></Box>}
								{enableFavorites && showStar && (
									<IconButton
										aria-label={isFav ? translate('sidebar.remove_from_favorites') : translate('sidebar.add_to_favorites')}
										size='xs'
										variant='ghost'
										flexShrink={0}
										minW='24px'
										w='24px'
										h='24px'
										color={isFav ? 'yellow.400' : starColor}
										icon={<Box as={isFav ? MdStar : MdStarBorder} w='14px' h='14px' />}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											toggleFavorite(route.path);
										}}
										_hover={{ color: 'yellow.500', bg: 'transparent' }}
									/>
								)}
							</Flex>
							<Box
								h='36px'
								w='4px'
								flexShrink={0}
								bg={isActive ? brandColor : 'transparent'}
								borderRadius='5px'
							/>
						</HStack>
					</Box>
				) : (
					<Box>
						<HStack spacing='26px' py='5px' ps='10px'>
							<Text
								me='auto'
								color={isActive ? activeColor : inactiveColor}
								fontWeight={isActive ? 'bold' : 'normal'}>
								{route.name}
							</Text>
							<Box h='36px' w='4px' bg={isActive ? 'brand.400' : 'transparent'} borderRadius='5px' />
						</HStack>
					</Box>
				)}
			</NavLink>
		);
	};

	const renderGroupedRoutes = () => {
		const ungroupedRoutes = otherRoutes.filter((r) => !r.group);
		const groupedEntries = GROUP_ORDER
			.map((group) => ({
				group,
				routes: otherRoutes.filter((r) => r.group === group),
			}))
			.filter((entry) => entry.routes.length > 0);

		return (
			<>
				{ungroupedRoutes.map((route, idx) => createLink(route, idx))}
				{groupedEntries.map(({ group, routes: groupRoutes }) => {
					const isExpanded = expandedGroups[group] ?? false;
					const label = translate(group as any) || group;
					return (
						<Box key={group} mt={isClubhouse ? '20px' : '12px'}>
							{/* Clubhouse: no divider line, just spacing. Classic: colored divider. */}
							{!isClubhouse && (
								<Divider
									borderColor={groupDividerColor}
									opacity={0.5}
									mx='10px'
									w='auto'
									mb='8px'
								/>
							)}
							<Flex
								align='center'
								cursor='pointer'
								data-tour={`sidebar-group/${group}`}
								onClick={() => toggleGroup(group)}
								ps='10px'
								pe='4px'
								py='4px'
								_hover={{ bg: 'transparent' }}
								userSelect='none'>
								<Text
									fontSize='10px'
									fontWeight='700'
									fontFamily='var(--heading-font, "Urbanist", "Figtree", sans-serif)'
									color={groupHeaderColor}
									textTransform='uppercase'
									letterSpacing='0.08em'
									flex='1'>
									{label}
								</Text>
								<Box as={isExpanded ? MdExpandLess : MdExpandMore} w='18px' h='18px' color={groupHeaderColor} />
							</Flex>
							<Collapse in={isExpanded} animateOpacity>
								{groupRoutes.map((route, idx) => createLink(route, idx))}
							</Collapse>
						</Box>
					);
				})}
			</>
		);
	};

	const renderFocusedMode = () => {
		if (favoriteRoutes.length === 0) {
			return (
				<Box textAlign='center' py='24px' px='16px'>
					<Text fontSize='sm' color={textColor} mb='12px'>
						{translate('sidebar.empty_favorites')}
					</Text>
					{onSwitchMode && (
						<Button
							size='sm'
							variant='outline'
							colorScheme='brand'
							onClick={() => onSwitchMode('full')}
						>
							{translate('sidebar.switch_to_full')}
						</Button>
					)}
				</Box>
			);
		}

		return (
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext items={favoriteRoutes.map((r) => r.path)} strategy={verticalListSortingStrategy}>
					{favoriteRoutes.map((route, idx) => (
						<SortableNavItem key={route.path} route={route}>
							{createLink(route, idx, false)}
						</SortableNavItem>
					))}
				</SortableContext>
			</DndContext>
		);
	};

	// Focused mode: show only favorites with drag-and-drop
	if (mode === 'focused' && enableFavorites) {
		return renderFocusedMode();
	}

	// Full mode: show all routes in groups (no separate FAVORITES/ALL TABS headers)
	return (
		<>
			{hasGroups ? renderGroupedRoutes() : otherRoutes.map((route, idx) => createLink(route, idx))}
		</>
	);
}

export default SidebarLinks;
